<template>
  <!-- This page supports infinite scroll -->
  <section
    class="templates-page"
    v-infinite-scroll="loadMoreTemplates"
    infinite-scroll-disabled="infiniteScrollDisabled"
    :infinite-scroll-distance="10"
  >
    <am2-put-email-template-name-modal
      :opened="displayRenameEmailTemplateModal"
      :loading="isUpdatingSelectedEmailTemplate"
      header="Rename Email Template"
      button-copy="Save"
      :default-name="emailTemplateToRename && emailTemplateToRename.meta.name"
      :default-description="emailTemplateToRename && emailTemplateToRename.meta.description"
      @confirm="handleRenameEmailTemplateConfirm"
      @cancel="handleRenameEmailTemplateCancel"
    />
    <am2-put-email-template-name-modal
      :opened="displayNewEmailTemplateModal"
      button-copy="Next"
      @confirm="handleNewEmailTempalteConfirm"
      @cancel="handleNewEmailTempalteCancel"
    />
    <div
      :class="[
        'content',
        $arMediaQuery.pageContent.maxWidth('sm') && 'sm-max',
      ]"
    >
      <am2-top
        :searchLeft="{
          action: handleTemplatesSearch,
          placeholder: 'Search templates',
          style: {
            width: searchBoxWidth,
            minWidth: '215px'
          }
        }"
        :style="{
          alignItems: 'flex-start',
        }"
      />
      <am2-tabs
        :selected-tab-index="selectedTemplateSourceIndex"
        @select="handleTemplateSourceSelect"
        :items="templateSourceItems"
        :style="{
          marginTop: '22px',
        }"
      />

      <div
        v-if="hasNoDisplayedEmailTemplates"
        class="no-templates-section"
      >
        <am2-no-content-section
          v-if="searchString"
          header="No templates found"
          header-size="sm"
        />
        <am2-no-content-section
          v-else
          icon-name="ar-template-in-circle"
          header="Create an email template"
          button-text="New template"
          @buttonClick="handleCreateTemplateClick"
        />
      </div>
      <div
        class="templates-wrapper"
      >
        <!-- Email Templates From Bucket Endpoint -->
        <am2-template-card
          v-for="emailTemplate of emailTemplates"
          :key="emailTemplate.oid"
          v-show="filterEmailTemplate(emailTemplate) && !isFetchingEmailTemplates"
          :class="[
            'template-card',
            $arMediaQuery.pageContent.maxWidth('lg') && 'lg-max',
            $arMediaQuery.pageContent.maxWidth('md') && 'md-max',
            $arMediaQuery.pageContent.maxWidth('xs') && 'xs-max',
          ]"
          :name="emailTemplate.meta.name"
          :description="emailTemplate.meta.description"
          :template="emailTemplate.meta.template"
          :template-html="emailTemplate.meta.url"
          :type="emailTemplate.meta.templateType"
          :thumbnail-url="emailTemplate.meta.thumbnailUrl"
          :allowed-actions="['edit', 'rename', 'delete']"
          @edit="handleEmailTemplateEdit(emailTemplate)"
          @rename="handleEmailTemplateRename(emailTemplate)"
          @delete="handleEmailTemplateDelete(emailTemplate)"
          @click="handleEmailTemplateEdit(emailTemplate)"
        />
        <!-- Email Templates From Beefree Bucket -->
        <am2-template-card
          v-for="beefreeTemplate of beefreeTemplates"
          :key="beefreeTemplate.id"
          v-show="filterBeefreeEmailTemplate(beefreeTemplate)"
          :class="[
            'template-card',
            $arMediaQuery.pageContent.maxWidth('lg') && 'lg-max',
            $arMediaQuery.pageContent.maxWidth('md') && 'md-max',
            $arMediaQuery.pageContent.maxWidth('xs') && 'xs-max',
          ]"
          :name="beefreeTemplate.name"
          :template="beefreeTemplate.design"
          type="beefree"
          :allowed-actions="['sendMessage', 'createTemplate']"
          :template-html="beefreeTemplate.url"
          :thumbnailUrl="beefreeTemplate.thumbnailUrl"
          @sendMessage="handleSendMessageFromBeefreeClick(beefreeTemplate)"
          @createTemplate="handleCreateTemplateFromEditorClick(beefreeTemplate)"
          @click="handleSendMessageFromBeefreeClick(beefreeTemplate)"
        />
        <am2-card-skeleton
          v-for="n in emailTemplatesSkeletonCardCount"
          :key="n"
          type="email-template"
          :class="[
            'template-card',
            $arMediaQuery.pageContent.maxWidth('lg') && 'lg-max',
            $arMediaQuery.pageContent.maxWidth('md') && 'md-max',
            $arMediaQuery.pageContent.maxWidth('xs') && 'xs-max',
          ]"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';

export default {
  name: 'Messages',
  layout: 'default',

  props: {
    createButtonClicked: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    searchString: '',
    emailTemplateToRename: null,
    editorTemplateToCreate: null,
    displayNewEmailTemplateModal: false,
    displayRenameEmailTemplateModal: false,
    templateSourceItems: [
      {
        title: 'Your templates',
        value: 'bucket',
      },
      {
        title: 'Library',
        value: 'beefree',
      },
    ],
    selectedTemplateSource: 'bucket',
    chosenEmailEditor: 'beefree',
  }),

  watch: {
    createButtonClicked(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.handleCreateTemplateClick();
        this.$emit("cancelCreateClicked");
      }
    }
  },

  computed: {
    ...mapState({
      beefreeTemplates: state => state.emailTemplate.beefreeTemplates,
      isFetchingBeefreeTemplates: state => state.emailTemplate.isFetchingBeefreeTemplates,
      emailTemplates: state => state.emailTemplate.emailTemplates,
      totalEmailTemplatesCount: state => state.emailTemplate.totalEmailTemplatesCount,
      isFetchingEmailTemplates: state => state.emailTemplate.isFetchingEmailTemplates,
      isUpdatingSelectedEmailTemplate: state => state.emailTemplate.isUpdatingSelectedEmailTemplate,
      hasNoMoreEmailTemplates: state => state.emailTemplate.hasNoMoreEmailTemplates,
      hasNoMoreBeefreeTemplates: state => state.emailTemplate.hasNoMoreBeefreeTemplates,
      totalBeefreeTemplatesCount: state => state.emailTemplate.totalBeefreeTemplatesCount,
    }),
    displayedEmailTemplatesCount() {
      return this.emailTemplates.filter(this.filterEmailTemplate).length;
    },
    hasNoDisplayedEmailTemplates() {
      return this.selectedTemplateSource === 'bucket' && this.displayedEmailTemplatesCount === 0 && !this.isFetchingEmailTemplates
    },
    selectedTemplateSourceIndex() {
      return this.templateSourceItems.findIndex(item => item.value === this.selectedTemplateSource);
    },
    emailTemplatesSkeletonCardCount() {
      if ((this.selectedTemplateSource === 'bucket' && !this.isFetchingEmailTemplates) ||
        (this.selectedTemplateSource === 'beefree' && !this.isFetchingBeefreeTemplates)) {
        return 0;
      }
      // Add number of skeleton cards to fill a new row the current row
      if (this.$arMediaQuery.pageContent.maxWidth('xs')) {
        return 3;
      } else if (this.$arMediaQuery.pageContent.maxWidth('md')) {
        return 6 + this.emailTemplates.length % 2;
      } else if (this.$arMediaQuery.pageContent.maxWidth('lg')) {
        return 9 + this.emailTemplates.length % 3;
      } else {
        return 12 + this.emailTemplates.length % 4;
      }
    },
    searchBoxWidth() {
      if (this.$arMediaQuery.pageContent.minWidth('md')) {
        return '330px';
      } else if (this.$arMediaQuery.pageContent.minWidth('sm')) {
        return 'calc(100% - 160px)'
      } else {
        return 'calc(100% - 80px)'
      }
    },
    infiniteScrollDisabled() {
      if (this.selectedTemplateSource === 'bucket') {
        return this.emailTemplates.length === this.totalEmailTemplatesCount;
      } else if (this.selectedTemplateSource === 'beefree') {
        return this.beefreeTemplates.length === this.totalBeefreeTemplatesCount;
      }

      return false;
    },
  },

  methods: {
    ...mapActions([
      'SHOW_CONFIRM',
      'emailTemplate/DELETE_EMAIL_TEMPLATE',
      'emailTemplate/DELETE_BEEFREE_EMAIL_TEMPLATE',
      'emailTemplate/FETCH_ALL_BEEFREE_EMAIL_TEMPLATES',
      'emailTemplate/FETCH_BEEFREE_TEMPLATES',
      'emailTemplate/UPDATE_EMAIL_TEMPLATE',
      'emailTemplate/UPDATE_BEEFREE_EMAIL_TEMPLATE',
    ]),

    ...mapMutations([
      'emailTemplate/RESET_EMAIL_TEMPLATES',
    ]),

    loadMoreTemplates() {
      if (this.chosenEmailEditor !== 'beefree') {
        return;
      }

      if ((this.selectedTemplateSource === 'bucket' && !!this.hasNoMoreEmailTemplates) ||
        (this.selectedTemplateSource === 'beefree' && !!this.hasNoMoreBeefreeTemplates)) {
        return;
      }

      // Check the appropriate loading flag based on the selected template source
      const isLoading = this.selectedTemplateSource === 'bucket'
        ? this.isFetchingEmailTemplates
        : this.isFetchingBeefreeTemplates;

      if (!isLoading) {
        const append = true;
        if (this.selectedTemplateSource === 'bucket') { // Saved Beefree templates
          this['emailTemplate/FETCH_ALL_BEEFREE_EMAIL_TEMPLATES']({skip: this.emailTemplates.length, append})
        } else { // Catalogue templates
          this['emailTemplate/FETCH_BEEFREE_TEMPLATES']({skip: this.beefreeTemplates.length, append})
        }
      }
    },
    filterEmailTemplate(emailTemplate) {
      return this.selectedTemplateSource === 'bucket'
        && this.$arFuzzySearch(emailTemplate.meta.name, this.searchString || '');
    },
    filterBeefreeEmailTemplate(beefreeTemplate) {
      return this.selectedTemplateSource === 'beefree'
        && this.$arFuzzySearch(beefreeTemplate.name, this.searchString || '');
    },
    displayTemplateSetupIsNotForMobileMessage() {
      this.SHOW_CONFIRM({
        messageHtml: `Template editing is only available on desktops and larger screens.`,
        confirmButtonText: 'Done',
        hideCancelButton: true,
      });
    },

    async askBeforeGoingToEmailMessageSetup() {
      if (this.$arMediaQuery.window.maxWidth('xs')) {
        const agreed = await this.SHOW_CONFIRM({
          messageHtml: `You are able to create and edit emails using your desktop or tablet device.`,
          hideConfirmButton: true,
          cancelButtonText: 'Back',
        });
        return agreed;
      } else {
        return true;
      }
    },
    handleTemplatesSearch(searchString) {
      this.searchString = searchString;
    },
    handleNewEmailTempalteConfirm({
      name,
      description,
    }) {
      this.$router.push({
        name: 'templates-new',
        query: {
          name,
          description,
          libraryTemplateId: this.editorTemplateToCreate?.id || null,
        },
        params: {
          templateType: this.chosenEmailEditor
        }
      });
    },
    handleNewEmailTempalteCancel() {
      this.displayNewEmailTemplateModal = false;
      this.editorTemplateToCreate = null;
    },
    async handleRenameEmailTemplateConfirm({
      name,
      description,
    }) {
      if (this.chosenEmailEditor === 'beefree') {
        const succeed = await this['emailTemplate/UPDATE_BEEFREE_EMAIL_TEMPLATE']({
          scratchEmailTemplate: {
            oid: this.emailTemplateToRename.oid,
            meta: {
              name,
              description,
              template: this.emailTemplateToRename.meta.template,
              templateType: this.emailTemplateToRename.meta.templateType,
              version: this.emailTemplateToRename.meta.version,
            },
          },
          displaySuccessMessage: false,
        });
        if (succeed) {
          this.displayRenameEmailTemplateModal = false;
        }
      } else {
        const succeed = await this['emailTemplate/UPDATE_EMAIL_TEMPLATE']({
          scratchEmailTemplate: {
            oid: this.emailTemplateToRename.oid,
            meta: {
              name,
              description,
              template: this.emailTemplateToRename.meta.template,
              templateType: this.emailTemplateToRename.meta.templateType,
            },
          },
          displaySuccessMessage: false,
        });
        if (succeed) {
          this.displayRenameEmailTemplateModal = false;
        }
      }
    },
    handleRenameEmailTemplateCancel() {
      this.displayRenameEmailTemplateModal = false;
    },
    async handleCreateTemplateClick() {
      if (this.$arMediaQuery.window.maxWidth('xs')) {
        this.displayTemplateSetupIsNotForMobileMessage();
        return;
      }
      this.displayNewEmailTemplateModal = true;
    },
    async handleEmailTemplateEdit(emailTemplate) {
      if (this.$arMediaQuery.window.maxWidth('xs')) {
        this.displayTemplateSetupIsNotForMobileMessage();
        return;
      }
      this.$router.push({
        name: 'templates-edit',
        params: {
          emailTemplateOid: emailTemplate.oid,
          templateType: this.chosenEmailEditor
        }
      });
    },
    handleEmailTemplateRename(emailTemplate) {
      this.emailTemplateToRename = emailTemplate;
      this.displayRenameEmailTemplateModal = true;
    },
    async handleEmailTemplateDelete(emailTemplate) {
      const agreed = await this.SHOW_CONFIRM({
        messageHtml: `Are you sure you want to delete the email template<br><b>${emailTemplate.meta.name}</b>?`,
      });
      if (agreed) {
        if (this.chosenEmailEditor === 'beefree') {
          this['emailTemplate/DELETE_BEEFREE_EMAIL_TEMPLATE'](emailTemplate.oid);
        } else {
          this['emailTemplate/DELETE_EMAIL_TEMPLATE'](emailTemplate.oid);
        }
      }
    },
    handleTemplateSourceSelect(templateTypeItem) {
      this.selectedTemplateSource = templateTypeItem.value;
    },
    async handleSendMessageFromBeefreeClick(beefreeTemplate) {
      const agreed = await this.askBeforeGoingToEmailMessageSetup();
      if (agreed) {
        this.$router.push({
          path: `/message-center/messages/email/new/template`,
          query: {
            libraryTemplateId: beefreeTemplate.oid,
          },
        });
      }
    },
    setEmailEditor(key) {
      this.chosenEmailEditor = key;
      this.initialiseBeefree()
    },
    initialiseBeefree() {
      this['emailTemplate/FETCH_ALL_BEEFREE_EMAIL_TEMPLATES']({skip: null, append: false});
      this['emailTemplate/FETCH_BEEFREE_TEMPLATES']({skip: null, append: false});
    },
  },

  created() {
    // If we can't find an email editor preference set by the admin, we use Beefree by default
    this['emailTemplate/RESET_EMAIL_TEMPLATES']()
    this.setEmailEditor('beefree')
  },
};
</script>

<style lang="scss" scoped>
.templates-page {
  display: flex;
  margin-top: 56px;
  max-width: 1296px;

  .content {
    width: 100%;
    margin-bottom: 50px;
    padding: 0 24px;

    .no-templates-section {
      margin-top: 28px;
    }

    .templates-wrapper {
      display: flex;
      justify-content: flex-start;
      flex-wrap: wrap;
      margin-top: 28px;
      margin-right: -20px;

      .template-card {
        width: calc(25% - 20px);
        margin-right: 20px;
        margin-bottom: 20px;
        &.lg-max {
          width: calc(33.3% - 20px);
          margin-right: 20px;
        }

        &.md-max {
          width: calc(50% - 20px);
          margin-right: 20px;
        }

        &.xs-max {
          width: 100%;
        }
      }

    }
  }
}
</style>
